/**
 * Комбобокс. Спецификация — docs/04 §5.2.3.
 *
 * Списки здесь бывают от пяти значений до ста тысяч (контрагенты, записи
 * справочника), поэтому виртуализация обязательна: рендерить 100 000 строк
 * ради выбора одной — это секунды простоя интерфейса.
 *
 * Поиск нечувствителен к регистру и к «ё»: пользователь ищет «Королев»,
 * а в справочнике «Королёв».
 */

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, Search } from "lucide-react";

import { cn } from "@/shared/lib/cn";

export interface ComboboxItem {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  items: readonly ComboboxItem[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  label?: string;
  /** Название для скринридера, когда подпись стоит рядом и повторять её незачем. */
  "aria-label"?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/** Порог, после которого список рендерится виртуализированно. */
const VIRTUALIZE_FROM = 100;
const ROW_HEIGHT = 32;
const MAX_LIST_HEIGHT = 320;

export function normalize(text: string): string {
  return text.toLowerCase().replaceAll("ё", "е");
}

export function Combobox({
  items,
  value,
  onValueChange,
  placeholder = "Не выбрано",
  searchPlaceholder = "Поиск…",
  emptyMessage = "Ничего не найдено",
  label,
  "aria-label": ariaLabel,
  disabled,
  className,
  id,
}: ComboboxProps): React.ReactElement {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    if (!query.trim()) {
      return items;
    }
    const needle = normalize(query);
    return items.filter((item) => normalize(item.label).includes(needle));
  }, [items, query]);

  const selected = items.find((item) => item.value === value) ?? null;

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  // При открытии список позиционируется на текущем значении, а не в начале
  React.useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const index = filtered.findIndex((item) => item.value === value);
    const target = index >= 0 ? index : 0;
    setActiveIndex(target);
    virtualizer.scrollToIndex(target, { align: "center" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  React.useEffect(() => {
    if (open && filtered.length > 0) {
      virtualizer.scrollToIndex(activeIndex, { align: "auto" });
    }
  }, [activeIndex, open, filtered.length, virtualizer]);

  function commit(index: number): void {
    const item = filtered[index];
    if (!item || item.disabled) {
      return;
    }
    onValueChange(item.value === value ? null : item.value);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(filtered.length - 1);
    } else if (event.key === "PageDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 10, filtered.length - 1));
    } else if (event.key === "PageUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 10, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      commit(activeIndex);
    }
  }

  const virtualized = filtered.length > VIRTUALIZE_FROM;
  const rows = virtualized
    ? virtualizer.getVirtualItems()
    : filtered.map((_, index) => ({
        index,
        key: index,
        start: index * ROW_HEIGHT,
        size: ROW_HEIGHT,
      }));

  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <label
          htmlFor={fieldId}
          className="mb-[var(--pt-space-4)] text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] font-medium text-[var(--pt-text-secondary)]"
        >
          {label}
        </label>
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            id={fieldId}
            type="button"
            role="combobox"
            {...(label === undefined && ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            disabled={disabled}
            className={cn(
              "inline-flex h-[var(--pt-control-h-md)] w-full items-center justify-between gap-[var(--pt-space-6)]",
              "rounded-[var(--pt-radius-3)] border border-[var(--pt-border-control)]",
              "bg-[var(--pt-bg-surface)] px-[var(--pt-pad-x)] text-left text-[var(--pt-font-ui)]",
              "transition-colors duration-[var(--pt-dur-micro)] hover:border-[var(--pt-border-strong)]",
              "focus:outline-none focus:shadow-[var(--pt-ring)]",
              "disabled:cursor-not-allowed disabled:bg-[var(--pt-bg-sunken)]",
              selected ? "text-[var(--pt-text-primary)]" : "text-[var(--pt-text-tertiary)]",
            )}
          >
            <span className="truncate">{selected?.label ?? placeholder}</span>
            <ChevronDown className="size-[var(--pt-icon)] shrink-0 text-[var(--pt-text-tertiary)]" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            onKeyDown={onKeyDown}
            className={cn(
              "z-[var(--pt-z-dropdown)] w-[var(--radix-popover-trigger-width)] max-w-[480px]",
              "overflow-hidden rounded-[var(--pt-radius-5)] border border-[var(--pt-border-default)]",
              "bg-[var(--pt-bg-raised)] shadow-[var(--pt-shadow-2)]",
            )}
          >
            <div className="flex items-center gap-[var(--pt-space-6)] border-b border-[var(--pt-border-subtle)] px-[var(--pt-space-8)]">
              <Search
                aria-hidden
                className="size-[var(--pt-icon)] text-[var(--pt-text-tertiary)]"
              />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-[var(--pt-control-h-md)] w-full bg-transparent text-[var(--pt-font-ui)] text-[var(--pt-text-primary)] placeholder:text-[var(--pt-text-tertiary)] focus:outline-none"
              />
            </div>

            <div
              ref={listRef}
              role="listbox"
              aria-label={label ?? ariaLabel ?? "Список значений"}
              className="overflow-auto p-[var(--pt-space-4)]"
              style={{ maxHeight: MAX_LIST_HEIGHT }}
            >
              {filtered.length === 0 ? (
                <p className="px-[var(--pt-space-8)] py-[var(--pt-space-12)] text-[var(--pt-fs-sm)] text-[var(--pt-text-tertiary)]">
                  {emptyMessage}
                </p>
              ) : (
                <div
                  style={{
                    height: virtualized ? virtualizer.getTotalSize() : undefined,
                    position: "relative",
                  }}
                >
                  {rows.map((row) => {
                    const item = filtered[row.index];
                    if (!item) {
                      return null;
                    }
                    const active = row.index === activeIndex;
                    return (
                      <div
                        key={item.value}
                        role="option"
                        aria-selected={item.value === value}
                        aria-setsize={filtered.length}
                        aria-posinset={row.index + 1}
                        onMouseEnter={() => setActiveIndex(row.index)}
                        onClick={() => commit(row.index)}
                        className={cn(
                          "flex cursor-default select-none items-center justify-between gap-[var(--pt-space-6)]",
                          "rounded-[var(--pt-radius-2)] px-[var(--pt-space-8)] text-[var(--pt-font-ui)]",
                          "text-[var(--pt-text-primary)]",
                          active && "bg-[var(--pt-bg-hover)]",
                          item.value === value && "bg-[var(--pt-bg-selected)]",
                          item.disabled && "text-[var(--pt-text-disabled)]",
                        )}
                        style={
                          virtualized
                            ? {
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: row.size,
                                transform: `translateY(${row.start}px)`,
                              }
                            : { height: ROW_HEIGHT }
                        }
                      >
                        <span className="truncate">{item.label}</span>
                        {item.value === value && (
                          <Check className="size-[var(--pt-icon)] shrink-0 text-[var(--pt-accent-text)]" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              aria-live="polite"
              className="border-t border-[var(--pt-border-subtle)] px-[var(--pt-space-8)] py-[var(--pt-space-4)] text-[var(--pt-fs-micro)] text-[var(--pt-text-tertiary)]"
            >
              {filtered.length > 0 ? `Найдено значений: ${filtered.length}` : emptyMessage}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
